<template>
  <section class="hlb-detail" :aria-busy="loading">
    <div class="hlb-title">原始签约信息</div>
    <div v-if="loading" class="hlb-hint" role="status">详情查询中...</div>
    <div v-else-if="error" class="hlb-error" role="alert">
      <span>详情查询失败：{{ error }}</span>
      <button v-if="externalAgreementNo" type="button" class="hlb-retry" @click="loadDetail">重试</button>
    </div>
    <template v-else-if="detail">
      <dl class="hlb-fields hlb-summary">
        <div><dt>交易类型</dt><dd>{{ detail.rt1_bizType || '-' }}</dd></div>
        <div><dt>商户编号</dt><dd class="hlb-mono">{{ detail.rt4_customerNumber || '-' }}</dd></div>
        <div><dt>返回码</dt><dd>{{ detail.rt2_retCode }}</dd></div>
        <div><dt>返回信息</dt><dd>{{ detail.rt3_retMsg || '-' }}</dd></div>
      </dl>
      <div v-if="!detail.rt5_bindCardList.length" class="hlb-hint">暂无交易卡绑定信息</div>
      <div
        v-for="(card, index) in detail.rt5_bindCardList"
        :key="card.bindId || index"
        class="hlb-card"
      >
        <div class="hlb-card-title">
          <span>交易卡 {{ index + 1 }}</span>
          <span class="hlb-status" :class="bindStatusClass(card.bindStatus)">{{ bindStatusText(card.bindStatus) }}</span>
        </div>
        <dl class="hlb-fields">
          <div><dt>姓名</dt><dd>{{ card.payerName || '-' }}</dd></div>
          <div><dt>手机号</dt><dd>{{ card.phone || '-' }}</dd></div>
          <div><dt>证件类型</dt><dd>{{ card.idCardType === 'IDCARD' ? '身份证' : (card.idCardType || '-') }}</dd></div>
          <div><dt>证件号</dt><dd class="hlb-mono">{{ card.idCardNo || '-' }}</dd></div>
          <div><dt>银行编码</dt><dd>{{ card.bankId || '-' }}</dd></div>
          <div><dt>借贷类型</dt><dd>{{ cardTypeText(card.cardType) }}</dd></div>
          <div><dt>卡号</dt><dd class="hlb-mono">{{ card.cardNo || '-' }}</dd></div>
          <div><dt>绑定号</dt><dd class="hlb-mono">{{ card.bindId || '-' }}</dd></div>
        </dl>
      </div>
    </template>
  </section>
</template>

<script>
import { getHelibaoAgreementStatus } from "@/utils/userQueryApi";
import { parseHelibaoAgreementStatus } from "@/utils/helibaoAgreement";

export default {
  name: "HelibaoAgreementDetails",
  props: {
    externalAgreementNo: { type: [String, Number], default: "" },
  },
  data() {
    return { loading: false, error: "", detail: null, requestId: 0 };
  },
  watch: {
    externalAgreementNo: {
      immediate: true,
      handler() {
        this.loadDetail();
      },
    },
  },
  beforeUnmount() {
    this.requestId += 1;
  },
  methods: {
    async loadDetail() {
      const requestId = ++this.requestId;
      const agreementNo = String(this.externalAgreementNo ?? "").trim();
      this.detail = null;
      this.error = "";
      this.loading = false;
      if (!agreementNo) {
        this.error = "缺少签约编号";
        return;
      }
      this.loading = true;
      try {
        const response = await getHelibaoAgreementStatus(agreementNo);
        if (requestId !== this.requestId) return;
        this.detail = parseHelibaoAgreementStatus(response);
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.error = error?.message || "网络异常，请稍后重试";
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    bindStatusText(status) {
      return {
        INIT: "初始化",
        SUCCESS: "绑定成功",
        FAIL: "绑定失败",
        DOING: "处理中",
        UNBIND: "已解绑",
      }[status] || status || "未知";
    },
    bindStatusClass(status) {
      return {
        SUCCESS: "hlb-status-green",
        FAIL: "hlb-status-red",
        DOING: "hlb-status-blue",
      }[status] || "hlb-status-gray";
    },
    cardTypeText(type) {
      return { DEBIT: "借记卡", CREDIT: "贷记卡" }[type] || type || "-";
    },
  },
};
</script>

<style scoped>
.hlb-detail {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e8e5e0;
  font-size: 13px;
  color: #37352f;
}
.hlb-title {
  margin-bottom: 8px;
  font-weight: 600;
}
.hlb-hint {
  padding: 8px 0;
  color: #6b6966;
}
.hlb-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 8px 20px;
  margin: 0;
}
.hlb-fields > div {
  display: flex;
  align-items: baseline;
  min-width: 0;
}
.hlb-fields dt {
  flex: 0 0 80px;
  color: #6b6966;
}
.hlb-fields dd {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
}
.hlb-summary {
  margin-bottom: 12px;
}
.hlb-card {
  margin-top: 8px;
  padding: 12px;
  border: 1px solid #e8e5e0;
  border-radius: 4px;
  background: #f8f7f5;
}
.hlb-card-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  font-weight: 500;
}
.hlb-mono {
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 12px;
}
.hlb-status {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
}
.hlb-status-green { background: #dbeddb; color: #2d6a2e; }
.hlb-status-red { background: #fbe4e4; color: #a63a32; }
.hlb-status-blue { background: #d3e5ef; color: #246b9e; }
.hlb-status-gray { background: #e8e5e0; color: #6b6966; }
.hlb-error {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #a63a32;
}
.hlb-retry {
  padding: 4px 12px;
  border: 1px solid #d0ccc5;
  border-radius: 4px;
  background: #fff;
  color: #37352f;
  font: inherit;
  cursor: pointer;
}
.hlb-retry:hover { background: #f0efed; }
.hlb-retry:focus-visible { outline: 2px solid #2878b5; outline-offset: 2px; }
</style>
